/**
 * Delivery areas measured along roads.
 *
 * "5 miles from the restaurant" used to mean a circle — straight-line, so an
 * address across water or with no direct road counted as near. These build the
 * area actually REACHABLE within a driving distance, using the Mapbox Isochrone
 * API, and return it in the same {lat,lng} ring shape the freehand draw tool
 * produces. Everything downstream still sees an ordinary polygon.
 *
 * Docs: https://docs.mapbox.com/api/navigation/isochrone/
 */

export const MILES_TO_KM = 1.60934
export const METERS_PER_MILE = 1609.344
// Quick picks. Any distance in between can be typed — see parseDrivingMiles.
export const DISTANCE_OPTIONS_MILES = [1, 2, 3, 5, 8, 10, 15, 20]
// Isochrone caps a contour at 100 km (~62 miles); 60 keeps a round margin.
export const MIN_MILES = 0.1
export const MAX_MILES = 60

/**
 * A driving distance an admin typed, as a number. Throws a message worth
 * showing if it isn't one we can ask Mapbox for.
 */
export function parseDrivingMiles(value) {
  const miles = typeof value === 'number' ? value : parseFloat(String(value).trim())
  if (!Number.isFinite(miles) || miles < MIN_MILES || miles > MAX_MILES) {
    throw new Error(`Driving distance must be between ${MIN_MILES} and ${MAX_MILES} miles.`)
  }
  return miles
}

/**
 * GeoJSON ring approximating a circle (radius km, center {lat,lng}).
 * Straight-line. Kept for callers that explicitly want a circle.
 */
export function circleToPolygonPath(center, radiusKm, numPoints = 64) {
  const R = 6371
  const points = []
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI
    const lat = center.lat + (radiusKm / R) * (180 / Math.PI) * Math.sin(angle)
    const lng =
      center.lng +
      ((radiusKm / R) * (180 / Math.PI) * Math.cos(angle)) / Math.cos((center.lat * Math.PI) / 180)
    points.push({ lat, lng })
  }
  return points
}

// The outer ring of the biggest polygon in an Isochrone response. denoise=1
// asks for one blob, but a MultiPolygon can still come back — take the ring
// with the most points rather than whichever landed first.
function largestRing(feature) {
  const geometry = feature && feature.geometry
  if (!geometry) return null
  if (geometry.type === 'Polygon') return geometry.coordinates[0]
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates
      .map(poly => poly[0])
      .reduce((best, ring) => (!best || ring.length > best.length ? ring : best), null)
  }
  return null
}

/**
 * The area reachable by car within `miles` of a point, following roads.
 *
 * @param {{lat:number,lng:number}} center  where the driving starts.
 * @param {number|string} miles             driving distance, 0.1–60.
 * @param {string} token                    Mapbox access token.
 * @returns {Promise<Array<{lat:number,lng:number}>>} ring for commitZonePath.
 * @throws {Error} with a message worth showing an admin — no token, no route,
 *   or Mapbox refusing. Callers surface it instead of quietly drawing a circle,
 *   because a circle is the very thing this replaces.
 */
export async function drivingAreaPath(center, miles, token) {
  if (!token) {
    throw new Error('No Mapbox token is configured, so a driving area cannot be built. Draw the area by hand instead.')
  }
  if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) {
    throw new Error('Restaurant location not available.')
  }
  // Isochrone takes whole metres, and throws out anything out of range first.
  const meters = Math.round(parseDrivingMiles(miles) * METERS_PER_MILE)

  const url =
    `https://api.mapbox.com/isochrone/v1/mapbox/driving/${center.lng},${center.lat}` +
    `?contours_meters=${meters}` +
    '&polygons=true' +
    // 1.0 keeps only the largest contour, so outlying pockets don't become
    // islands the point-in-polygon check would miss anyway.
    '&denoise=1' +
    // Smooth the ring a little. Tight enough not to move the edge meaningfully,
    // loose enough that a 20-mile area isn't thousands of points in the DB.
    '&generalize=50' +
    `&access_token=${encodeURIComponent(token)}`

  let res
  try {
    res = await fetch(url)
  } catch (e) {
    throw new Error('Could not reach Mapbox to measure driving distance. Check the connection and try again.')
  }
  if (!res.ok) {
    const detail = await res.json().catch(() => null)
    throw new Error(
      detail && detail.message
        ? `Mapbox could not build the area: ${detail.message}`
        : `Mapbox could not build the area (HTTP ${res.status}).`
    )
  }

  const data = await res.json()
  const ring = largestRing((data.features || [])[0])
  if (!ring || ring.length < 3) {
    throw new Error('No roads reachable from this location within that distance. Check the restaurant pin, or draw the area by hand.')
  }
  // GeoJSON closes its ring by repeating the first point; the draw tool's paths
  // do not, so drop it.
  const open = ring.length > 1 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring
  return open.map(([lng, lat]) => ({ lat, lng }))
}
